import { ChevronDown, Loader2, Monitor } from 'lucide-react';

export function Card({
  title,
  icon,
  children,
  action,
  className = '',
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-[22px] border border-black/[0.06] bg-white/90 p-5 text-left shadow-[0_8px_30px_-18px_rgba(15,23,42,0.18)] backdrop-blur-sm ${className}`}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#F2F4F7] text-[var(--dash-ink)]">
              {icon}
            </span>
          ) : null}
          <h3 className="m-0 truncate text-[15px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
            {title}
          </h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function StatCell({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="min-w-0 rounded-[16px] bg-[#F7F8FA] px-3.5 py-3.5">
      <p className="m-0 flex items-center gap-1.5 text-[11px] font-medium tracking-[-0.01em] text-[var(--dash-muted)]">
        <span className="opacity-70">{icon}</span>
        {label}
      </p>
      <p className="m-0 mt-1.5 break-words text-[15px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
        {value ?? '—'}
      </p>
    </div>
  );
}

export function MiniStat({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="rounded-[12px] bg-[#F7F8FA] px-2.5 py-2">
      <p className="m-0 text-[10px] font-medium text-[var(--dash-muted)]">{label}</p>
      <p className="m-0 mt-0.5 text-[13px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
        {value ?? '—'}
      </p>
    </div>
  );
}

export function PriceRow({
  label,
  value,
  bold,
  green,
}: {
  label: string;
  value?: string | number | null;
  bold?: boolean;
  green?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span
        className={
          bold
            ? 'text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]'
            : 'text-[13px] text-[var(--dash-muted)]'
        }
      >
        {label}
      </span>
      <span
        className={`text-right tabular-nums ${
          bold
            ? 'text-[15px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]'
            : green
              ? 'text-[13px] font-semibold text-emerald-600'
              : 'text-[13px] font-medium text-[var(--dash-ink)]'
        }`}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

export function KpiCard({
  label,
  value,
  color,
  bg,
}: {
  label: string;
  value: number | string;
  color: string;
  bg: string;
}) {
  return (
    <div
      className={`rounded-[20px] border border-black/[0.05] p-4 text-center shadow-[0_8px_24px_-16px_rgba(15,23,42,0.2)] ${bg}`}
    >
      <p className={`m-0 text-2xl font-semibold tracking-[-0.03em] ${color}`}>
        {value}
      </p>
      <p className="m-0 mt-1 text-[11px] font-medium text-[var(--dash-muted)]">
        {label}
      </p>
    </div>
  );
}

export function FilterDropdown({
  label,
  value,
  onChange,
  options,
  allLabel = 'All',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  allLabel?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium text-[var(--dash-muted)]">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cursor-pointer appearance-none rounded-[12px] border border-black/[0.06] bg-[#F7F8FA] py-2.5 pr-9 pl-3.5 text-sm font-semibold text-[var(--dash-ink)] outline-none transition focus:border-[var(--dash-brand)]/40 focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15"
        >
          <option value="">{allLabel}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]"
          aria-hidden
        />
      </div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return (
    <div className="rounded-[22px] border border-dashed border-black/[0.08] bg-white/80 px-4 py-16 text-center">
      <Monitor className="mx-auto h-8 w-8 text-[var(--dash-muted)] opacity-30" />
      <p className="mt-3 mb-0 text-sm font-semibold text-[var(--dash-ink)]">{text}</p>
    </div>
  );
}
